import { useConnectedCrmIntegrations } from "@/hooks/useConnectedCrmIntegrations";
import { useTeams } from "@/contexts/teams-provider";
import { useTranslation } from "@/hooks/useTranslation";
import type { PeopleRowsUpdate } from "@/hooks/usePeopleTableRowsWriter";
import { EmployeeDTO, HubSpotSaveToListSyncedRecordDTO } from "@shaarp/common";
import { GlobeIcon, MapPinIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ButtonFacetedFilter,
  FacetedFilterOption,
} from "../button-faceted-filter";
import { DataTable } from "../data-table/data-table";
import { dataTableEmployeesColumns } from "./data-table-company-employees-columns";
import { DataTableToolbarEmployeesExport } from "./data-table-toolbar-employees-export";
import { DataTableToolbarEmployeesListingAction } from "./data-table-toolbar-employees-listing-action";

export default function DataTableCompanyEmployees({
  employees,
  companyId = null,
  pagination = true,
  showTotalCounter = true,
  emptyText,
  isLoading = false,
  onRemoveFromListing,
  searchQuery,
  showAssignedColumn = true,
  forceContactActions = false,
  onRowsChange,
  areRowActionsLocked = false,
}: {
  employees: EmployeeDTO[];
  companyId?: string | null;
  pagination?: boolean;
  showFilter?: boolean;
  showTotalCounter?: boolean;
  emptyText?: string;
  isLoading?: boolean;
  onRemoveFromListing?: (employee: EmployeeDTO) => void;
  searchQuery?: string;
  showAssignedColumn?: boolean;
  /**
   * When true (search results), always offer the email/phone reveal buttons
   * and the CRM sync button even when the contact has no resolved company.
   */
  forceContactActions?: boolean;
  /**
   * Every change the table makes to its own rows — an email or a phone
   * revealed, a person synced to the CRM — for a caller that keeps the rows
   * elsewhere too: the chat's people table, read from the query cache. Left
   * unset, the change only lives in the table, as on every other page.
   */
  onRowsChange?: (update: PeopleRowsUpdate) => void;
  /**
   * Shows the reveal buttons disabled and leaves out the CRM sync, the lists
   * and the assignment of each row: the chat's table while its search fills
   * it, whose rows are replaced on every streamed one — a reveal clicked then
   * would be lost, and paid for again. Left unset, every action is offered.
   */
  areRowActionsLocked?: boolean;
}) {
  const { t, currentLanguage } = useTranslation();
  const [data, setData] = useState<EmployeeDTO[]>([]);
  const connectedCrmIntegrations = useConnectedCrmIntegrations();
  const { teamSelected } = useTeams();

  const showIntegrationsColumn = connectedCrmIntegrations.length > 0;

  const showEmailColumn =
    teamSelected?.featureContactRevealEmailEnabled ?? true;
  const showPhoneColumn =
    teamSelected?.featureContactRevealPhoneEnabled ?? true;

  const [countriesSelected, setCountriesSelected] = useState<string[]>([]);
  const [countriesOptions, setCountriesOptions] = useState<
    FacetedFilterOption[]
  >([]);

  const [citiesSelected, setCitiesSelected] = useState<string[]>([]);
  const [citiesOptions, setCitiesOptions] = useState<FacetedFilterOption[]>([]);

  useEffect(() => {
    setData(employees);

    const uniqueCountries = [
      ...new Set(
        employees
          .filter((employee) => employee.country != null)
          .map((employee) => employee.country)
      ),
    ];
    setCountriesOptions(
      uniqueCountries
        .map((country) => ({
          label: country!,
          value: country!,
          total: employees.filter((e) => e.country === country).length,
          icon: GlobeIcon,
        }))
        .sort((a, b) => b.total - a.total)
    );

    const uniqueCities = [
      ...new Set(
        employees
          .filter((employee) => employee.city !== null && employee.city !== undefined)
          .map((employee) => employee.city)
      ),
    ];
    setCitiesOptions(
      uniqueCities
        .map((city) => ({
          label: city!,
          value: city!,
          total: employees.filter((e) => e.city === city).length,
          icon: MapPinIcon,
        }))
        .sort((a, b) => b.total - a.total)
    );
  }, [employees]);

  const updateRows = useCallback(
    (update: PeopleRowsUpdate) => {
      setData(update);
      onRowsChange?.(update);
    },
    [onRowsChange]
  );

  const onRowChanged = useCallback(
    (employeeUpdate: Partial<EmployeeDTO> & { id: string }) => {
      updateRows((prev) =>
        prev.map((employee) =>
          employee.id === employeeUpdate.id
            ? { ...employee, ...employeeUpdate }
            : employee
        )
      );
    },
    [updateRows]
  );

  const memoizedColumns = useMemo(
    () =>
      dataTableEmployeesColumns(
        companyId,
        onRowChanged,
        showIntegrationsColumn,
        onRemoveFromListing,
        t,
        currentLanguage,
        showAssignedColumn,
        forceContactActions,
        showEmailColumn,
        showPhoneColumn,
        areRowActionsLocked,
      ),
    [companyId, onRowChanged, showIntegrationsColumn, onRemoveFromListing, t, currentLanguage, showAssignedColumn, forceContactActions, showEmailColumn, showPhoneColumn, areRowActionsLocked]
  );

  const onEmployeesSynced = (syncedRecords: HubSpotSaveToListSyncedRecordDTO[]) => {
    const syncMap = new Map(syncedRecords.map((r) => [r.id, r]));
    updateRows((prev) =>
      prev.map((employee) => {
        const synced = syncMap.get(employee.id);
        if (!synced) return employee;
        return {
          ...employee,
          integrations: {
            ...employee.integrations,
            hubspot: {
              isSynced: true,
              externalId: synced.externalId,
              directLink: synced.directLink ?? employee.integrations?.hubspot?.directLink ?? null,
            },
          },
        };
      })
    );
  };

  return (
    <DataTable
      data={data.filter((employee) => {
        if (
          countriesSelected.length > 0 &&
          !countriesSelected.includes(employee.country!)
        ) {
          return false;
        }
        if (employee.suggestedOrder !== null) {
          return true;
        }
        if (citiesSelected.length > 0) {
          return citiesSelected.includes(employee.city!);
        }
        return true;
      })}
      columns={memoizedColumns}
      pagination={pagination}
      showFilter={true}
      showTotalCounter={isLoading ? false : showTotalCounter}
      emptyText={
        emptyText || t("components.dataTableCompanyEmployees.emptyText")
      }
      sortingState={[
        {
          id: "Depuis",
          desc: false,
        },
      ]}
      isLoadingStreaming={isLoading}
      toolbarContent={
        !isLoading && (
          <div className="flex gap-2 w-full">
            <ButtonFacetedFilter
              title={t("components.dataTableCompanyEmployees.filters.city")}
              compacted={false}
              options={citiesOptions}
              selected={citiesSelected}
              onChange={setCitiesSelected}
            />
            <ButtonFacetedFilter
              title={t("components.dataTableCompanyEmployees.filters.country")}
              compacted={false}
              options={countriesOptions}
              selected={countriesSelected}
              onChange={setCountriesSelected}
            />
            <DataTableToolbarEmployeesListingAction
              employees={data}
              fetchDataLoading={isLoading}
              searchQuery={searchQuery}
            />
            <DataTableToolbarEmployeesExport
              employees={data}
              companyId={companyId ?? null}
              fetchDataLoading={isLoading}
              onEmployeesSynced={onEmployeesSynced}
            />
          </div>
        )
      }
    />
  );
}
